// ─────────────────────────────────────────────────────────────────────────────
// Camada de plataforma — a ponte entre o React e o mundo lá fora.
//
// O Orion agora roda em dois lugares: dentro do Electron (desktop) e dentro do
// navegador (web). Quase tudo funciona igual nos dois, mas algumas coisas só
// existem no desktop: arrastar a janela sem moldura, minimizar para a bandeja,
// gravar um PDF numa pasta do Windows, ler o histórico de um arquivo local.
//
// Em vez de espalhar `if (estáNoElectron)` pelas telas, TODA conversa com o
// sistema operacional passa por aqui. Cada função tem duas versões: a do
// desktop (via IPC) e a da web (o equivalente mais próximo possível, ou nada).
// As telas chamam a função e não precisam saber onde estão rodando.
// ─────────────────────────────────────────────────────────────────────────────

// ── Detecção de ambiente ─────────────────────────────────────────────────────
// `window.orion` é criado pelo preload (electron/preload.js) e só existe no
// desktop. No navegador ele é undefined — é isso que separa os dois mundos.
//
// A versão anterior fazia `window.require('electron')`, o que exigia
// `nodeIntegration: true` na janela: a página tinha Node inteiro à disposição.
// Agora a página não tem Node nenhum, e o que ela alcança é a lista fechada de
// canais que o preload expõe.
const ponte = (typeof window !== 'undefined' && window.orion) ? window.orion : null;

/** true quando o app está rodando dentro do Electron (desktop). */
export const ehDesktop = !!ponte;

/** true quando o app está rodando no navegador (web). */
export const ehWeb = !ehDesktop;

// ── Qual tela esta janela deve mostrar ───────────────────────────────────────
// O desktop abre várias janelas do MESMO bundle; o hash diz qual é qual.
// Sem hash → o Orion inteiro. '#expandido' → o ticket formalizado.
export function ehJanelaExpandida() {
  return typeof window !== 'undefined' && window.location.hash.startsWith('#expandido');
}

// ── Envio e escuta de mensagens ──────────────────────────────────────────────
// Na web viram no-ops silenciosos: a tela pede, nada acontece, nada quebra.

/** Manda um recado para o processo principal (sem esperar resposta). */
export function enviar(canal, ...args) {
  if (ponte) ponte.enviar(canal, ...args);
}

/** Pede algo ao processo principal e espera a resposta. */
export function pedir(canal, ...args) {
  if (ponte) return ponte.pedir(canal, ...args);
  return Promise.resolve(null);
}

/**
 * Escuta um recado vindo do processo principal.
 * Devolve uma função para cancelar a escuta — use no cleanup do useEffect,
 * senão cada re-render empilha um listener novo e o mesmo evento roda várias vezes.
 */
export function escutar(canal, callback) {
  if (!ponte) return () => {};
  return ponte.escutar(canal, callback);
}

// ── Janela ───────────────────────────────────────────────────────────────────
// No navegador a janela é do usuário, não do app: nada aqui faz sentido, então
// tudo vira no-op. As telas continuam chamando sem se preocupar.
// Os três estados são os mesmos do Nexus:
//   button   → o app é um disco de 68px no canto da tela;
//   expanded → janela de ~82% × 86% da área útil, redimensionável;
//   maximizado (dentro de expanded) → 100% da área útil.
export const janela = {
  /** Botão flutuante → janela expandida. */
  expandir: () => enviar('expand'),

  /** Alterna entre a janela de 82% e 100% da área útil. */
  alternarMaximizado: () => enviar('toggle-maximize'),

  /** Janela → botão flutuante. */
  recolherParaBotao: () => enviar('minimize-to-button'),

  /** O X da barra: esconde para a bandeja (não encerra o app). */
  fechar: () => enviar('window-close'),

  /** Traz de volta da bandeja. */
  restaurar: () => enviar('request-restore'),

  /** Começo do arrasto manual da janela sem moldura. */
  iniciarArrasto: (screenX, screenY) => enviar('start-drag', { screenX, screenY }),

  /** Movimento do arrasto. */
  arrastar: (screenX, screenY) => enviar('dragging', { screenX, screenY }),

  /** Fim do arrasto — o processo principal confere se a janela ficou alcançável. */
  pararArrasto: () => enviar('stop-drag'),

  /** Deixa cliques atravessarem a janela. */
  ignorarMouse: (ignorar, opcoes = {}) => enviar('set-ignore-mouse-events', ignorar, opcoes),

  /** Avisa quando o modo muda ('button' | 'expanded'). Devolve o cancelador. */
  aoMudarModo: (callback) => escutar('window-mode', modo => callback(modo)),

  /** Avisa quando entra/sai de 100% da área útil. Devolve o cancelador. */
  aoMudarMaximizado: (callback) => escutar('window-maximized', cheia => callback(cheia)),

  /** Avisa quando o app volta da bandeja. Devolve o cancelador. */
  aoRestaurar: (callback) => escutar('app-restored', () => callback()),
};

// ── Versão do app ────────────────────────────────────────────────────────────
/**
 * Versão exibida no título e no cabeçalho. No desktop vem do package.json do
 * app; na web vem de REACT_APP_VERSAO, que o script `build` do package.json
 * preenche com o próprio `version` (o navegador não tem como perguntar).
 *
 * Sem número fixo de reserva: um '2.0.0' escrito aqui ficava para trás a cada
 * versão e a web mostrava a versão errada sem ninguém notar. Sem versão, o
 * cabeçalho simplesmente não a exibe.
 */
export async function versaoApp() {
  const v = await pedir('get-app-version');
  return v || process.env.REACT_APP_VERSAO || '';
}

// ── Configuração persistida ──────────────────────────────────────────────────
// Desktop: arquivo config.json na pasta do usuário (via IPC).
// Web: localStorage — mesmo formato, mesma chave, mesmo comportamento.
const CHAVE_CONFIG_WEB = 'orion:config';

export const config = {
  /**
   * Lê a configuração salva. No desktop ela chega por evento ('config-loaded'),
   * então aqui a leitura direta só vale para a web.
   */
  ler: () => {
    if (ehDesktop) return null; // no desktop quem entrega é o evento abaixo
    try {
      return JSON.parse(localStorage.getItem(CHAVE_CONFIG_WEB) || '{}');
    } catch (e) {
      return {};
    }
  },

  /** Salva a configuração inteira. */
  salvar: (dados) => {
    if (ehDesktop) { enviar('save-config', dados); return; }
    try { localStorage.setItem(CHAVE_CONFIG_WEB, JSON.stringify(dados)); } catch (e) {}
  },

  /** Escuta a configuração que o processo principal envia ao abrir. */
  aoCarregar: (callback) => escutar('config-loaded', dados => callback(dados)),
};

// ── Cofre de segredos ────────────────────────────────────────────────────────
//
// Usado por uma coisa só: a senha guardada quando o operador marca "lembrar
// credenciais".
//
// O Orion 1.x gravava essa senha em TEXTO PLANO no config.json da pasta do
// usuário. Qualquer processo rodando na conta dele lia a senha da Space num
// arquivo .json.
//
// No desktop, o processo principal cifra com `safeStorage`, que usa a chave do
// usuário no sistema operacional (DPAPI no Windows): o texto cifrado não serve
// em outra conta nem em outra máquina.
//
// Na web NÃO HÁ equivalente. localStorage é texto plano e legível por qualquer
// script da página. Então na web a senha simplesmente não é guardada — o
// "lembrar" passa a valer só para o e-mail. É menos cômodo e é o certo.
export const segredo = {
  /** Cifra um texto para poder guardá-lo. Na web devolve '' (não guarda). */
  guardar: async (texto) => {
    if (!texto) return '';
    if (!ehDesktop) return '';
    return (await pedir('segredo-guardar', texto)) || '';
  },

  /** Decifra o que foi guardado. '' quando não dá para ler. */
  ler: async (cifrado) => {
    if (!cifrado || !ehDesktop) return '';
    return (await pedir('segredo-ler', cifrado)) || '';
  },

  /** true quando dá para guardar senha com segurança neste ambiente. */
  disponivel: ehDesktop,
};

// ── Histórico de formalizações ───────────────────────────────────────────────
// Desktop: history.json na pasta do usuário. Web: localStorage, mesmo limite.
const CHAVE_HISTORICO_WEB = 'orion:historico';
const MAX_HISTORICO = 200;

function lerHistoricoWeb() {
  try { return JSON.parse(localStorage.getItem(CHAVE_HISTORICO_WEB) || '[]'); }
  catch (e) { return []; }
}

export const historico = {
  /** Lista as formalizações salvas, da mais recente para a mais antiga. */
  carregar: async () => {
    if (ehDesktop) return (await pedir('load-history')) || [];
    return lerHistoricoWeb();
  },

  /** Insere uma entrada nova ou atualiza a existente pelo id. */
  salvarEntrada: (entrada) => {
    if (ehDesktop) { enviar('save-history-entry', entrada); return; }
    let lista = lerHistoricoWeb();
    const i = lista.findIndex(e => e.id === entrada.id);
    if (i !== -1) lista[i] = { ...lista[i], ...entrada };
    else {
      lista.unshift(entrada);
      if (lista.length > MAX_HISTORICO) lista = lista.slice(0, MAX_HISTORICO);
    }
    try { localStorage.setItem(CHAVE_HISTORICO_WEB, JSON.stringify(lista)); } catch (e) {}
  },

  /** Apaga tudo. */
  limpar: () => {
    if (ehDesktop) { enviar('clear-history'); return; }
    try { localStorage.setItem(CHAVE_HISTORICO_WEB, '[]'); } catch (e) {}
  },
};

// ── PDF do registro de incidente ─────────────────────────────────────────────
// Desktop: o processo principal renderiza e grava em Documentos\Orion\PDF.
// Web: não há pasta do usuário — a tela deve oferecer download do arquivo.
export const pdf = {
  /** Gera o PDF. Devolve { ok, filePath?, error? } no desktop, ou { ok:false, web:true }. */
  gerar: async (payload) => {
    if (ehDesktop) return await pedir('gerar-pdf-erro', payload);
    return { ok: false, web: true, error: 'Geração de PDF em pasta local só existe no aplicativo desktop.' };
  },

  /**
   * Grava em Documentos\Orion\PDF um PDF montado no cliente.
   * No navegador não há pasta do usuário: devolve { ok:false, web:true } e quem
   * chamou segue sem arquivo local — o documento já foi enviado ao n8n.
   */
  salvar: async (nome, bytes) => {
    if (ehDesktop) return await pedir('salvar-pdf-erro', { nome, bytes });
    return { ok: false, web: true };
  },

  /** Abre o PDF gerado no visualizador do sistema. */
  abrir: (caminho) => enviar('abrir-pdf-erro', caminho),

  /** Abre a pasta onde os PDFs são gravados. */
  abrirPasta: () => enviar('abrir-pasta-pdf'),
};

// ── Área de transferência ────────────────────────────────────────────────────
/**
 * Copia texto.
 *
 * Usa `navigator.clipboard` nos dois ambientes: o Chromium do Electron tem a
 * mesma API do navegador, então não é preciso trazer o módulo `clipboard` do
 * Electron para dentro da página — e um canal a menos na ponte é um canal a
 * menos para revisar.
 */
export async function copiarTexto(texto) {
  // Devolve se copiou DE VERDADE. Antes esta função respondia `resolve()` sem
  // copiar quando não havia área de transferência, e a tela dizia "copiado"
  // para um Ctrl+C que não aconteceu.
  //
  // `navigator.clipboard` só funciona em contexto seguro (https, localhost ou
  // o file:// do app empacotado). Servido por HTTP interno, ele existe e
  // REJEITA — daí o try/catch e o caminho antigo como reserva.
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch (e) { /* cai na reserva abaixo */ }

  try {
    const campo = document.createElement('textarea');
    campo.value = texto;
    // Fora da vista e sem rolar a página ao receber foco.
    campo.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(campo);
    campo.select();
    const copiou = document.execCommand('copy');
    document.body.removeChild(campo);
    return !!copiou;
  } catch (e) {
    return false;
  }
}

// ── Links externos ───────────────────────────────────────────────────────────
/**
 * Abre um link fora do app.
 *
 * O destino é conferido AQUI e DE NOVO no processo principal (ver
 * `podeAbrirExterno` em electron/main.js). A checagem dupla é de propósito:
 * boa parte destas URLs vem do banco (anexo de chamado de T.I., documento do
 * Drive), e no desktop `shell.openExternal` entrega o endereço ao sistema
 * operacional — `file://`, um caminho de rede ou um esquema registrado por
 * outro programa não podem chegar lá.
 */
export function abrirExterno(url) {
  if (!url) return;

  let protocolo;
  try {
    protocolo = new URL(url, window.location.href).protocol;
  } catch (e) {
    console.warn('[Orion] Endereço externo inválido.');
    return;
  }

  if (protocolo !== 'http:' && protocolo !== 'https:') {
    console.warn(`[Orion] Recusado: só http e https podem ser abertos (veio "${protocolo}").`);
    return;
  }

  if (ehDesktop) { enviar('open-external', url); return; }
  window.open(url, '_blank', 'noopener,noreferrer');
}

// ── Janela expandida do ticket formalizado ───────────────────────────────────
export const janelaExpandida = {
  /** Abre a janela com o ticket formalizado. Na web abre uma aba com o mesmo hash. */
  abrir: (dados) => {
    if (ehDesktop) { enviar('open-expanded-view', dados); return; }
    try { sessionStorage.setItem('orion:expandido', JSON.stringify(dados)); } catch (e) {}
    window.open(`${window.location.pathname}#expandido`, '_blank', 'noopener,noreferrer');
  },

  /** Recebe os dados na janela recém-aberta. Devolve o cancelador da escuta. */
  aoReceberDados: (callback) => {
    if (ehDesktop) return escutar('set-data', dados => callback(dados));
    // Na web os dados ficam no sessionStorage porque não há IPC entre abas.
    try {
      const bruto = sessionStorage.getItem('orion:expandido');
      if (bruto) callback(JSON.parse(bruto));
    } catch (e) {}
    return () => {};
  },
};

// ── Atualização automática ───────────────────────────────────────────────────
export const atualizacao = {
  /** Avisa quando há uma atualização baixada esperando reinício. */
  aoFicarPronta: (callback) => escutar('update-ready', () => callback()),

  /** Instala a atualização e reinicia (só desktop). */
  instalar: () => enviar('install-update'),

  /** Procura versão nova agora. Resolve com o estado ao fim da busca (null na web). */
  verificar: () => pedir('verificar-atualizacao'),

  /** Estado atual, para quem abre depois que a busca da inicialização já correu. */
  estado: () => pedir('estado-atualizacao'),

  /** Cada mudança de fase: verificando, baixando (com percentual), pronto, erro... */
  aoMudarEstado: (callback) => escutar('update-status', estado => callback(estado)),
};
